import { computed, inject, Injectable, signal } from '@angular/core';
import {
  injectInfiniteQuery,
  injectMutation,
  injectQuery,
  injectQueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { Auth } from 'src/providers/auth/auth';

import { MonthlyFinanceApiService } from './monthly-finance-api.service';
import { dashboardWidgetsKeys } from './dashboard-widgets-queries.service';
import { MonthAttributionService } from './month-attribution.service';
import {
  MonthAttribution,
  MonthlyFinanceAudit,
  MonthlyFinanceKind,
  MonthlyFinanceOverrideEntry,
  MonthlyFinanceProfitAndLoss,
} from 'src/models/monthly-finance.model';

const AUDITS_PER_PAGE = 25;

export const monthlyFinanceKeys = {
  all: ['monthly-finance'] as const,
  profitAndLoss: (addressId: number | null, year: number, kind: MonthlyFinanceKind | null, attribution: MonthAttribution) =>
    [...monthlyFinanceKeys.all, 'profit-and-loss', addressId, year, kind, attribution] as const,
  audits: (addressId: number | null, year: number | null, kind: MonthlyFinanceKind | null, attribution: MonthAttribution) =>
    [...monthlyFinanceKeys.all, 'audits', addressId, year, kind, attribution] as const,
};

@Injectable({ providedIn: 'root' })
export class MonthlyFinanceQueriesService {
  private readonly api = inject(MonthlyFinanceApiService);
  private readonly auth = inject(Auth);
  private readonly queryClient = injectQueryClient();
  private readonly monthAttribution = inject(MonthAttributionService);

  readonly addressId = signal<number | null>(null);
  readonly year = signal<number>(new Date().getFullYear());
  readonly kind = signal<MonthlyFinanceKind | null>(null);

  readonly profitAndLossQuery = injectQuery(() => {
    const addressId = this.addressId();
    const year = this.year();
    const kind = this.kind();
    const attribution = this.monthAttribution.value();
    return {
      queryKey: monthlyFinanceKeys.profitAndLoss(addressId, year, kind, attribution),
      queryFn: () =>
        lastValueFrom(
          this.api.getProfitAndLoss({
            start_year: year,
            start_month: 1,
            end_year: year,
            end_month: 12,
            address_id: addressId ?? undefined,
            kind: kind ?? undefined,
            month_attribution: attribution,
          }),
        ),
      staleTime: 60 * 1000,
      enabled: this.auth.isAuthenticated(),
    };
  });

  readonly profitAndLoss = computed<MonthlyFinanceProfitAndLoss | null>(
    () => this.profitAndLossQuery.data() ?? null,
  );

  readonly isLoadingProfitAndLoss = computed(() => this.profitAndLossQuery.isPending());

  readonly overrideMutation = injectMutation(() => ({
    mutationFn: (entries: MonthlyFinanceOverrideEntry[]) =>
      lastValueFrom(this.api.upsertOverrides(entries)),
    // Returned so the mutation settles only once the grid has refetched. A caller that drops
    // its typed value on success would otherwise show the old override until the refetch lands.
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: [...monthlyFinanceKeys.all, 'audits'] });
      // Editing a P&L cell also affects the Overview/Strategy widgets
      // (KPI tiles, P&L Trends, P&L Summary). Invalidating here ensures the
      // next time Strategy fetches — either from normal stale-time expiry
      // or from the route-reuse reattach refetch — it pulls fresh numbers.
      this.queryClient.invalidateQueries({ queryKey: dashboardWidgetsKeys.all });
      return this.queryClient.invalidateQueries({ queryKey: [...monthlyFinanceKeys.all, 'profit-and-loss'] });
    },
  }));

  readonly auditsQuery = injectInfiniteQuery(() => {
    const addressId = this.addressId();
    const year = this.year();
    const kind = this.kind();
    const attribution = this.monthAttribution.value();
    return {
      queryKey: monthlyFinanceKeys.audits(addressId, year, kind, attribution),
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        lastValueFrom(
          this.api.listAudits({
            page: pageParam as number,
            per_page: AUDITS_PER_PAGE,
            address_id: addressId ?? undefined,
            start_year: year,
            start_month: 1,
            end_year: year,
            end_month: 12,
            kind: kind ?? undefined,
            month_attribution: attribution,
          }),
        ),
      getNextPageParam: (lastPage: MonthlyFinanceAudit[], _allPages, lastPageParam) =>
        lastPage.length < AUDITS_PER_PAGE ? undefined : (lastPageParam as number) + 1,
      enabled: this.auth.isAuthenticated(),
      staleTime: 30 * 1000,
    };
  });

  readonly audits = computed<MonthlyFinanceAudit[]>(() => {
    const pages = this.auditsQuery.data()?.pages ?? [];
    return pages.flat();
  });

  loadMoreAudits(): void {
    if (this.auditsQuery.hasNextPage() && !this.auditsQuery.isFetchingNextPage()) {
      this.auditsQuery.fetchNextPage();
    }
  }
}
